import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS } from '../TemplateEditor/mergeFieldDefinitions';

// Import sibling components directly rather than via the components barrel
// (`'../../components'`). The barrel is a re-export hub that other files
// inside src/components/ also pull from — fine in production builds, but
// during dev-server HMR sessions, a brand-new component added to the barrel
// can have its module graph fail to reconcile, surfacing as the cryptic
// "originalFactory is undefined" runtime error. Direct imports avoid the
// cycle entirely.
import Button from '../Button/Button';
import Modal from '../Modal/Modal';

import css from './CreateTemplateModal.module.css';

// Mirrors VALID_DOCUMENT_TYPES + VALID_SIGNER_ROLES in
// server/api/document-template-utils.js. See the rationale comment there
// for the semantic differences between the types. Order here is
// presentation order in the dropdown — Contract first because it's the
// most semantically distinct (single-signer, check-in gate); Agreement
// before Waiver because the two share binding semantics and the only
// difference is provider-facing label.
//
// Push 9 Sub-phase B — two new inspection document_types added at the end
// of the list: pre-departure first, then post-return (chronological flow
// order). Inspections use signer_role='renter' on both Party A (provider)
// and Party B (customer) signing-link rows — the provider-vs-customer
// distinction lives on document_signing_links.party_role, not on the
// template. The CreateTemplateModal's signer-role auto-select logic
// (effect at L82-94) extends naturally because the new types appear in
// VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS with the single 'renter' value.
//
// Phase A.4a: 'custom' removed from SIGNER_ROLES (server-side validator
// rejects on CREATE going forward). Signer-role dropdown is now filtered
// per-document_type via VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS.
const DOCUMENT_TYPES = [
  'contract',
  'waiver',
  'agreement',
  'inspection_pre_departure',
  'inspection_post_return',
];
const NAME_MAX_LENGTH = 200;

/**
 * CreateTemplateModal — captures the three immutable-after-creation fields
 * (name, document_type, signer_role) needed to mint a new template. On
 * submit the parent dispatches createDocumentTemplate; on success the parent
 * redirects to the editor page using the duck's lastCreatedId.
 *
 * Form state is local (controlled inputs) — react-final-form is overkill for
 * three fields, and the codebase's other in-house modals (CancelBookingModal)
 * use the same controlled-input pattern.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling - from parent's mapDispatchToProps
 * @param {(values: { name, document_type, signer_role }) => Promise} props.onSubmit
 * @param {boolean} [props.isSubmitting] - disables Create + shows in-progress state
 * @param {Object|null} [props.error] - storableError shape; rendered inline above the form
 */
const CreateTemplateModal = props => {
  const intl = useIntl();
  const {
    isOpen = false,
    onClose,
    onManageDisableScrolling,
    onSubmit,
    isSubmitting = false,
    error,
  } = props;

  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [signerRole, setSignerRole] = useState('');

  // Reset form fields whenever the modal closes so reopening shows fresh state.
  // Don't reset on open — the user may have just opened it and we shouldn't
  // wipe what they're typing if `isOpen` flickers true→true via re-render.
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDocumentType('');
      setSignerRole('');
    }
  }, [isOpen]);

  // Phase A.4a: auto-select / filter signer_role when document_type changes.
  // Contract has only one valid role ('renter') — auto-select + hide the
  // dropdown so the provider doesn't think there's a choice. Waiver/agreement
  // has two valid roles — clear the field if the previous selection is no
  // longer valid (e.g., user picked 'renter' under contract, then switched
  // to waiver where 'renter' isn't allowed).
  useEffect(() => {
    // Contract + the two inspection types all have exactly one valid signer
    // role ('renter') — auto-select so the dropdown isn't shown. The render
    // path's `isContractType`-driven hide-dropdown logic is expanded below
    // via `isSingleSignerRoleType` to cover the inspection types too.
    if (
      documentType === 'contract' ||
      documentType === 'inspection_pre_departure' ||
      documentType === 'inspection_post_return'
    ) {
      setSignerRole('renter');
    } else if (documentType) {
      const validRoles = VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS[documentType] || [];
      if (signerRole && !validRoles.includes(signerRole)) {
        setSignerRole('');
      }
    }
    // Intentionally only react to documentType changes — including signerRole
    // would loop on the setSignerRole calls. The closure reads the latest
    // signerRole at effect-fire time, which is what we want.
  }, [documentType]);

  const validSignerRoles = documentType
    ? VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS[documentType] || []
    : [];
  // Contract has only one valid signer ('renter'). A.4a fix-pass v2:
  // render the signer_role field as a styled locked display (NOT a
  // disabled <select>) so the provider sees a clear "this is fixed"
  // affordance instead of a placeholder-grey dropdown that looks empty.
  //
  // Push 9 Sub-phase B — both inspection types share the contract
  // pattern (only 'renter' is valid). The render path checks
  // `isSingleSignerRoleType` rather than `isContractType` to extend
  // the locked-display affordance to inspections.
  const isContractType = documentType === 'contract';
  const isSingleSignerRoleType =
    isContractType ||
    documentType === 'inspection_pre_departure' ||
    documentType === 'inspection_post_return';

  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length <= NAME_MAX_LENGTH;
  const canSubmit = nameValid && documentType && signerRole && !isSubmitting;

  const handleSubmit = e => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      name: trimmedName,
      document_type: documentType,
      signer_role: signerRole,
    });
  };

  return (
    <Modal
      id="CreateTemplateModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'CreateTemplateModal.close' })}
    >
      <h2 className={css.modalTitle}>
        <FormattedMessage id="CreateTemplateModal.title" />
      </h2>

      {error ? (
        <p className={css.errorLine}>
          <FormattedMessage
            id="CreateTemplateModal.createError"
            values={{ error: error.message || error.toString() }}
          />
        </p>
      ) : null}

      <form className={css.form} onSubmit={handleSubmit} noValidate>
        <label className={css.field}>
          <span className={css.label}>
            <FormattedMessage id="CreateTemplateModal.nameLabel" />
          </span>
          {/* See RenameTemplateModal for the rationale on omitting maxLength
              — keep client and server validation symmetric and let the user
              see (and edit down) over-limit input rather than silently
              truncating their paste. */}
          <input
            type="text"
            className={css.input}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={intl.formatMessage({ id: 'CreateTemplateModal.namePlaceholder' })}
            autoFocus
            required
          />
          {trimmedName.length > NAME_MAX_LENGTH ? (
            <span className={css.fieldError}>
              <FormattedMessage id="CreateTemplateModal.nameTooLong" />
            </span>
          ) : null}
        </label>

        {/* A.4a fix-pass v2: replace native <select> with a radio-row
            list so all three options' descriptions and the Contract
            "Required for check-in" badge are visible upfront (educational
            signal at decision time, not after). The fieldset/legend
            wrapper groups the three rows for screen readers. */}
        <fieldset className={css.documentTypeFieldset}>
          <legend className={css.label}>
            <FormattedMessage id="CreateTemplateModal.documentTypeLabel" />
          </legend>
          <div className={css.docTypeRows}>
            {DOCUMENT_TYPES.map(t => {
              const isSelected = documentType === t;
              return (
                <label
                  key={t}
                  className={
                    isSelected ? `${css.docTypeRow} ${css.docTypeRowSelected}` : css.docTypeRow
                  }
                >
                  <input
                    type="radio"
                    name="documentType"
                    value={t}
                    checked={isSelected}
                    onChange={e => setDocumentType(e.target.value)}
                    className={css.docTypeRadio}
                  />
                  <div className={css.docTypeContent}>
                    <div className={css.docTypeHeading}>
                      <span className={css.docTypeName}>
                        <FormattedMessage id={`CreateTemplateModal.documentType.${t}`} />
                      </span>
                      {t === 'contract' ? (
                        <span className={css.requiredForCheckInBadge}>
                          <FormattedMessage id="CreateTemplateModal.documentType.badge.requiredForCheckIn" />
                        </span>
                      ) : null}
                    </div>
                    <div className={css.docTypeDescription}>
                      <FormattedMessage id={`CreateTemplateModal.documentType.description.${t}`} />
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </fieldset>

        {/* Phase A.4a fix-pass v2: signer_role section. Two render paths:
            (1) Contract — locked display (styled <div>, not a <select>)
                showing "🔒 Renter / Customer" in black-on-grey. Communicates
                "this is fixed" instead of looking like an unfilled dropdown.
            (2) Waiver / Agreement — interactive <select> filtered to
                ['operator', 'participant']. Also handles the no-document-
                type-yet initial state (disabled select with placeholder).
            Helper text below the field branches between contract-specific
            and generic copy. */}
        {isSingleSignerRoleType ? (
          <div className={css.field}>
            <span className={css.label}>
              <FormattedMessage id="CreateTemplateModal.signerRoleLabel" />
            </span>
            <div className={css.lockedField} aria-disabled="true">
              <span className={css.lockIcon} aria-hidden="true">
                🔒
              </span>
              <span className={css.lockedFieldText}>
                <FormattedMessage id="CreateTemplateModal.signerRole.renter" />
              </span>
            </div>
            <span className={css.fieldHelp}>
              <FormattedMessage
                id={
                  isContractType
                    ? 'CreateTemplateModal.signerRole.helpText.contract'
                    : 'CreateTemplateModal.signerRole.helpText.inspection'
                }
              />
            </span>
          </div>
        ) : (
          <label className={css.field}>
            <span className={css.label}>
              <FormattedMessage id="CreateTemplateModal.signerRoleLabel" />
            </span>
            <select
              className={css.select}
              value={signerRole}
              onChange={e => setSignerRole(e.target.value)}
              disabled={!documentType}
              required
            >
              {/* Empty placeholder for both initial state (no documentType
                  chosen yet) AND for waiver/agreement-with-no-role-picked. */}
              {!signerRole ? (
                <option value="">
                  {intl.formatMessage({ id: 'CreateTemplateModal.selectPlaceholder' })}
                </option>
              ) : null}
              {validSignerRoles.map(r => (
                <option key={r} value={r}>
                  {intl.formatMessage({ id: `CreateTemplateModal.signerRole.${r}` })}
                </option>
              ))}
            </select>
            <span className={css.fieldHelp}>
              <FormattedMessage id="CreateTemplateModal.signerRoleHelp" />
            </span>
          </label>
        )}

        <div className={css.buttons}>
          <Button
            type="button"
            className={css.cancelButton}
            onClick={onClose}
            disabled={isSubmitting}
          >
            <FormattedMessage id="CreateTemplateModal.cancelButton" />
          </Button>
          <Button
            type="submit"
            className={css.createButton}
            disabled={!canSubmit}
            inProgress={isSubmitting}
          >
            <FormattedMessage
              id={
                isSubmitting
                  ? 'CreateTemplateModal.creatingButton'
                  : 'CreateTemplateModal.createButton'
              }
            />
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTemplateModal;
